import type { AdPlacement } from '@openx/types/adUnit.ts';
import type { DistributionChannel } from '@openx/types/distributionChannel.ts';
import { useOldAdPlacementOptionsFetch } from '@openx/utils/apiHooks/useOldAdPlacementOptionsFetch.ts';
import { useOldDistributionChannelFetch } from '@openx/utils/apiHooks/useOldDistributionChannelOptionsFetch.ts';
import { useMemo } from 'react';
import type { DealLibraryItem } from '../../../types/index.ts';
import { mapAdPlacementLabels } from '../utils/mapAdPlacementLabels.ts';
import { mapDeviceTypeStringsToNames } from '../utils/mapDeviceTypeStringsToNames.ts';
import { mapDistributionChannelLabels } from '../utils/mapDistributionChannelLabels.ts';

export const useMappedDealTemplates = (dealTemplates: DealLibraryItem[]): DealLibraryItem[] => {
  const { adPlacement } = useOldAdPlacementOptionsFetch();
  const { distributionChannel } = useOldDistributionChannelFetch();

  return useMemo(() => {
    return dealTemplates.map(dealTemplate => {
      const { targeting, ...rest } = dealTemplate;
      const rendering_context = targeting?.rendering_context;

      const device_type = {
        name: [
          rendering_context?.device_type?.desktop_devices,
          rendering_context?.device_type?.mobile_devices,
          rendering_context?.device_type?.tv_devices,
        ]
          .filter(Boolean)
          .map(deviceTypeString => mapDeviceTypeStringsToNames(deviceTypeString!))
          .join(', '),
        op: rendering_context?.device_type?.op ?? null,
      };

      const distribution_channel = rendering_context?.distribution_channel?.val
        ? mapDistributionChannelLabels(
            rendering_context.distribution_channel.val as DistributionChannel,
            distributionChannel
          )
        : null;

      const ad_placement = rendering_context?.ad_placement?.val
        ? mapAdPlacementLabels(rendering_context.ad_placement.val as AdPlacement, adPlacement)
        : null;

      return { ...rest, ad_placement, device_type, distribution_channel };
    });
  }, [dealTemplates, distributionChannel, adPlacement]);
};
